// Stage 2: 简历获取&筛选 - 3个模块
import { useApi, api, mutate } from '../api/client';
import { useActions } from '../actions';
import {
  PageHeader, ModuleBanner, Card, CardHead, Kpi, Tag, Status, Loading, ErrorBox, Progress,
} from '../components/UI';
import { MODULE_META } from '../config';

// ===== 2.1 渠道发布 =====
export function ChannelOps() {
  const meta = MODULE_META['channel-ops'];
  const { openCreate } = useActions();
  const { data, loading, error } = useApi(() => api.channelPosts(), [], 'channel-posts');
  if (loading) return <Loading />;
  if (error || !data) return <ErrorBox message={error || ''} />;

  return (
    <div className="fade-in">
      <PageHeader
        title={meta.title}
        subtitle={meta.subtitle}
        stageBadge={meta.stageBadge}
        actions={<button className="btn btn-primary" onClick={() => openCreate('channel-posts')}>+ 发布岗位</button>}
      />
      <ModuleBanner title="岗位在各渠道的实时状态" stageTag="Stage 2-1" />

      <div className="grid grid-4" style={{ marginBottom: 24 }}>
        <Kpi label="在招岗位" value={28} />
        <Kpi label="今日浏览" value="4,044" trend={{ type: 'up', text: '↑ 18%' }} />
        <Kpi label="今日投递" value={177} trend={{ type: 'up', text: '↑ 26%' }} />
        <Kpi label="主动打招呼" value={42} />
      </div>

      <Card>
        <CardHead title="发布中岗位" />
        <table className="table">
          <thead>
            <tr>
              <th>岗位</th><th>发布渠道</th><th>最后刷新</th><th>状态</th>
              <th>浏览</th><th>投递</th><th>主动推荐</th>
            </tr>
          </thead>
          <tbody>
            {data.map(p => (
              <tr key={p.id}>
                <td><b>{p.position}</b></td>
                <td className="text-sm">{p.channels}</td>
                <td className="mono text-xs">{p.refresh}</td>
                <td><Status type={p.status === '在线' ? 'progress' : 'pending'}>{p.status}</Status></td>
                <td className="mono">{p.views}</td>
                <td className="mono fw-600 text-cinnabar">{p.apply}</td>
                <td className="mono">{p.push}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ===== 2.2 简历筛选 =====
export function ResumeScreen() {
  const meta = MODULE_META['resume-screen'];
  const { toast } = useActions();
  const { data, loading, error } = useApi(() => api.resumeFilter(), [], 'resume-filter');
  if (loading) return <Loading />;
  if (error || !data) return <ErrorBox message={error || ''} />;

  const pass = data.filter(r => r.status === '通过').length;
  const reject = data.filter(r => r.status === '淘汰').length;
  const hold = data.filter(r => r.status === '待定').length;

  return (
    <div className="fade-in">
      <PageHeader
        title={meta.title}
        subtitle={meta.subtitle}
        stageBadge={meta.stageBadge}
        actions={
          <>
            <button className="btn btn-ghost" onClick={() => toast('请先勾选简历')}>批量操作</button>
            <button className="btn btn-primary" onClick={() => { const p = (data || []).filter(r => r.status === '待定'); if (!p.length) return toast('没有待推进的简历'); Promise.all(p.map(r => mutate.update('resume-filter', r.id, { status: '通过' }))).then(() => toast('已推进 ' + p.length + ' 人', 'success')); }}>批量推进</button>
          </>
        }
      />
      <ModuleBanner title="今日待筛选简历" stageTag="Stage 2-2" />

      <div className="grid grid-4" style={{ marginBottom: 24 }}>
        <Kpi label="今日新收" value={62} />
        <Kpi label="已筛选" value={48} unit="份" />
        <Kpi label="智能匹配" value={87} unit="%" />
        <Kpi label="平均用时" value="3.2" unit="min" />
      </div>

      <Card>
        <CardHead
          title="简历筛选工作台"
          sub={`通过 ${pass} · 待定 ${hold} · 淘汰 ${reject}`}
        />
        <table className="table">
          <thead>
            <tr>
              <th>候选人</th><th>应聘岗位</th><th>年限</th><th>学历</th>
              <th>期望薪资</th><th>来源</th><th>匹配度</th><th>状态</th>
              <th>筛选理由</th><th>操作</th>
            </tr>
          </thead>
          <tbody>
            {data.map(r => (
              <tr key={r.id}>
                <td>
                  <div className="name-cell">
                    <div className="avatar-sm">{r.avatar}</div>
                    <b>{r.name}</b>
                  </div>
                </td>
                <td className="text-sm">{r.role}</td>
                <td>{r.years}年</td>
                <td>{r.edu}</td>
                <td className="mono text-xs">{r.salary}</td>
                <td><Tag color="outline">{r.source}</Tag></td>
                <td>
                  <div className="flex items-center gap-2">
                    <span className="mono fw-600" style={{ color: r.match >= 80 ? 'var(--bamboo)' : r.match >= 60 ? 'var(--gold)' : 'var(--cinnabar)' }}>
                      {r.match}
                    </span>
                    <div className="progress" style={{ width: 50 }}>
                      <div className={`progress-bar ${r.match >= 80 ? 'bamboo' : 'gold'}`} style={{ width: `${r.match}%` }} />
                    </div>
                  </div>
                </td>
                <td>
                  {r.status === '通过' && <Tag color="bamboo">{r.status}</Tag>}
                  {r.status === '待定' && <Tag color="gold">{r.status}</Tag>}
                  {r.status === '淘汰' && (
                    <span className="tag" style={{ background: 'var(--paper-3)', color: 'var(--ink-3)' }}>{r.status}</span>
                  )}
                </td>
                <td className="text-sm text-ink-3" style={{ maxWidth: 200 }}>{r.reason}</td>
                <td><button className="btn-text" onClick={() => toast('简历详情开发中')}>详情</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ===== 2.3 人才库 =====
export function TalentPool() {
  const meta = MODULE_META['talent-pool'];
  const { openCreate } = useActions();
  const { data, loading, error } = useApi(() => api.candidates(), [], 'candidates');
  if (loading) return <Loading />;
  if (error || !data) return <ErrorBox message={error || ''} />;

  return (
    <div className="fade-in">
      <PageHeader
        title={meta.title}
        subtitle={meta.subtitle}
        stageBadge={meta.stageBadge}
        actions={<button className="btn btn-primary" onClick={() => openCreate('activity')}>+ 主动激活</button>}
      />
      <ModuleBanner title="Talent Pool · 2,847 份优质简历" stageTag="Stage 2-3" />

      <div className="grid grid-4" style={{ marginBottom: 24 }}>
        <Kpi label="人才库总量" value="2,847" />
        <Kpi label="高潜人才" value={428} />
        <Kpi label="本月激活" value={38} unit="次" />
        <Kpi label="标签覆盖" value={126} />
      </div>

      <Card>
        <CardHead title="热门标签" />
        <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
          <Tag color="cinnabar">React (328)</Tag>
          <Tag color="cinnabar">Java (412)</Tag>
          <Tag color="bamboo">算法 (186)</Tag>
          <Tag color="bamboo">产品经理 (274)</Tag>
          <Tag color="gold">B 端经验 (487)</Tag>
          <Tag color="gold">高并发 (231)</Tag>
          <Tag>架构师 (98)</Tag>
          <Tag>字节背景 (156)</Tag>
          <Tag>阿里背景 (203)</Tag>
          <Tag>大厂 P7+ (342)</Tag>
          <Tag>硕士及以上 (612)</Tag>
          <Tag>海归 (87)</Tag>
          <Tag>稳定性强 (524)</Tag>
          <Tag>+ 添加</Tag>
        </div>
      </Card>

      <ModuleBanner title="高匹配候选人" stageTag="基于 AI 智能匹配" />

      <div className="candidate-grid">
        {data.map(c => (
          <div key={c.id} className={`candidate-card ${c.star ? 'star' : ''}`}>
            <div className="cand-head">
              <div className="cand-avatar">{c.avatar}</div>
              <div className="cand-info">
                <div className="cand-name">{c.name}</div>
                <div className="cand-title">{c.role} · {c.company}</div>
              </div>
            </div>
            <div className="cand-meta">
              <Tag>{c.years}年</Tag>
              <Tag>{c.edu}</Tag>
              <Tag>{c.salary}</Tag>
            </div>
            <div className="cand-match">
              {c.tags.slice(0, 3).map((t, i) => (
                <div key={i} className="cand-match-row">
                  <span>{t}</span>
                  <span className="text-bamboo">✓</span>
                </div>
              ))}
            </div>
            <div className="cand-foot">
              <span>匹配度</span>
              <span className="mono fw-600 text-cinnabar">{c.match}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
